import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface UseSEOProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  noindex?: boolean;
}

/**
 * Hook to manage SEO settings for the page
 * Automatically sets title and meta tags based on the current route
 */
export const useSEO = ({
  title,
  description,
  canonicalPath,
  noindex = false
}: UseSEOProps = {}) => {
  const location = useLocation();
  const baseUrl = window.location.origin;
  
  useEffect(() => {
    // Set page title
    if (title) {
      document.title = title.includes('1% A Day') 
        ? title 
        : `${title} | 1% A Day`;
    }
    
    // Find or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    
    // Set meta description
    if (description) {
      metaDescription.setAttribute('content', description);
    }
    
    // Find or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    
    // Set canonical URL
    const canonicalUrl = canonicalPath 
      ? `${baseUrl}${canonicalPath}` 
      : `${baseUrl}${location.pathname}`;
    canonicalLink.setAttribute('href', canonicalUrl);
    
    // Handle robots meta tag
    let robotsTag = document.querySelector('meta[name="robots"]');
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    
    // Set robots directive
    robotsTag.setAttribute(
      'content', 
      noindex 
        ? 'noindex, nofollow' 
        : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
    );
    
    // Add structured data for breadcrumbs
    addBreadcrumbStructuredData(location.pathname);
    
    // Clean up on unmount
    return () => {
      // Do not remove shared elements, just reset them if needed
    };
  }, [title, description, location.pathname, canonicalPath, noindex, baseUrl]);
};

/**
 * Adds structured data for breadcrumbs based on the current path
 */
const addBreadcrumbStructuredData = (path: string) => {
  // Remove old breadcrumb data if exists
  const oldScript = document.querySelector('script[data-breadcrumb="true"]');
  if (oldScript) {
    oldScript.remove();
  }
  
  // Skip for homepage
  if (path === '/') return;
  
  // Split path into segments
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return;
  
  // Create breadcrumb items
  const itemListElement = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": window.location.origin
    }
  ];
  
  // Build breadcrumb trail
  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Format the segment name for display (capitalize, replace hyphens)
    const name = segment
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
      
    itemListElement.push({
      "@type": "ListItem",
      "position": index + 2,
      "name": name,
      "item": `${window.location.origin}${currentPath}`
    });
  });
  
  // Create structured data
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": itemListElement
  };
  
  // Add to document
  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-breadcrumb', 'true');
  script.textContent = JSON.stringify(breadcrumbData);
  document.head.appendChild(script);
};